import React, { useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Autoplay, Lazy, A11y } from "swiper";
import "swiper/swiper-bundle.css";
import sal from "sal.js";
import AppointmentForm from "../../form/AppointmentForm";

SwiperCore.use([Navigation, Autoplay, Lazy, A11y]);

const DoctorsSection = ({ doctors = [] }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [showModal, setShowModal] = React.useState(false);
  const [selectedDoctor, setSelectedDoctor] = React.useState(null);

  const openModal = (doctor) => {
    setSelectedDoctor(doctor);
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  useEffect(() => {
    document.querySelectorAll(".doctor-card").forEach((card, index) => {
      card.setAttribute("data-sal", "slide-up");
      card.setAttribute("data-sal-duration", "300");
      card.setAttribute("data-sal-delay", 50 + index * 50);
    });
    sal({ threshold: 0.2, once: true });
  }, [doctors]);

  const getSlidesPerView = (desired) => {
    return doctors.length < desired ? doctors.length : desired;
  };

  const isSpecialtiesPage = location.pathname.toLowerCase().includes("specialties");

  const specialty_name = doctors[0].specialty_name ?? null;

  if (!doctors || doctors.length === 0) return null;

  return (
    <section className="doctors-section">
      <h2 className={`section-title ${isSpecialtiesPage ? "text-capitalize mb-5" : ""}`} data-sal="fade" data-sal-duration="300">
        {isSpecialtiesPage ? (
          <>Meet the Minds Behind Exceptional <br /> {specialty_name}</>
        ) : (
          "Our Doctors"
        )}
      </h2>

      {!isSpecialtiesPage && <p className="section-subtitle" data-sal="fade" data-sal-duration="300">Lifeline Multispeciality Consultants</p>}

      <div className={`container doctors-slider position-relative ${doctors.length === 1 ? "single-doctor-slider-container" : ""}`}>
        <button type="button" className="slick-prev">
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <button type="button" className="slick-next">
          <i className="fa-solid fa-chevron-right"></i>
        </button>

        {doctors.length === 1 ? (
          <div className="single-doctor-wrapper single d-flex justify-content-center">
            <div className="doctor-card">
              <img
                src={
                  doctors[0].image_url?.startsWith("http")
                    ? doctors[0].image_url
                    : `${process.env.REACT_APP_WEB_URL}/${doctors[0].image_url}`
                }
                alt={doctors[0].name}
                className="doctor-img"
                onClick={() => navigate(`/doctor/${doctors[0].slug}`)}
              />
              <div className="doctor-info">
                <h5>{doctors[0].title}</h5>
                <h3>{doctors[0].name}</h3>
                <p className="qualifications">{doctors[0].qualifications}</p>

                {doctors[0].expertise?.length > 0 && doctors[0].expertise?.[0] !== "" && (
                  <>
                    <h4>Expertise in</h4>
                    <ul>
                      {doctors[0].expertise.map((exp, i) => (
                        <li key={i}>{exp}</li>
                      ))}
                    </ul>
                  </>
                )}

                <button
                  className="book-appointment-btn"
                  onClick={() => openModal(doctors[0])}
                >
                  BOOK APPOINTMENT
                </button>
              </div>
            </div>
          </div>
        ) : (

          <Swiper
            navigation={{
              prevEl: ".slick-prev",
              nextEl: ".slick-next",
            }}
            autoplay={
              doctors.length > 4
                ? {
                  delay: 7000,
                  disableOnInteraction: false,
                }
                : false
            }
            loop={doctors.length > 4}
            lazy={true}
            a11y={{ enabled: true }}
            spaceBetween={20}
            slidesPerView={getSlidesPerView(4)}
            breakpoints={{
              992: { slidesPerView: getSlidesPerView(4) },
              768: { slidesPerView: getSlidesPerView(2) },
              0: { slidesPerView: getSlidesPerView(1) },
            }}
            onInit={(swiper) => setTimeout(() => swiper.update(), 50)}
            className="gastro-doctors-swiper"
          >
            {doctors.map((doc, idx) => (
              <SwiperSlide key={idx}>
                <div className={`doctor-card ${doctors.length === 2 ? "two-doctors" : ""}`}>
                  <img
                    src={
                      doc.image_url?.startsWith("http")
                        ? doc.image_url
                        : `${process.env.REACT_APP_WEB_URL}/${doc.image_url}`
                    }
                    alt={doc.name}
                    className="doctor-img"
                    onClick={() => navigate(`/doctor/${doc.slug}`)}
                    loading="lazy"
                  />
                  <div className="doctor-info">
                    <div onClick={() => navigate(`/doctor/${doc.slug}`)}>
                      <h5>{doc.title}</h5>
                      <h3>{doc.name}</h3>
                      <p className="qualifications">{doc.qualifications}</p>
                      {doc.expertise && doc.expertise.length > 0 && (
                        <>
                          <h4>Expertise in</h4>
                          <ul>
                            {doc.expertise.map((exp, i) => (
                              <li key={i}>{exp}</li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                    <button className="book-appointment-btn" onClick={() => openModal(doc)}>
                      BOOK APPOINTMENT
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </div>

      <div className="row pt-3 pt-md-5">
        <div className="col-12">
          <Link
            to="/doctorslist"
            className="about-btn"
            data-sal="slide-up"
            data-sal-duration="1000"
            data-sal-delay="100"
          >
            {isSpecialtiesPage ? "VIEW ALL SPECIALISTS" : "MORE DOCTORS"}
          </Link>
        </div>
      </div>

      <AppointmentForm show={showModal} handleClose={closeModal} doctorName={selectedDoctor?.name} />
    </section>
  );
};

export default DoctorsSection;
